import { hasFeature } from "../features.js";
import { useCallback, useEffect, useState } from "react";
import { money } from "@baros/core";
import { useApp } from "../context.js";
import type { ScreenProps } from "../screens.js";
import { Activity } from "./Activity.js";
import { orgApi, OrgApiError, type OrgMember, type OrgOverview } from "./api.js";
import { DataExport } from "./DataExport.js";
import { Integrations } from "./Integrations.js";
import { Tablets } from "./Tablets.js";
import { Members } from "./Members.js";
import { Settings } from "./Settings.js";
import { Venues } from "./Venues.js";
import "./org.css";

type Tab = "members" | "venues" | "settings" | "integrations" | "tablets" | "activity" | "data";

const TABS: readonly { readonly id: Tab; readonly label: string }[] = [
  { id: "members", label: "Members" },
  { id: "venues", label: "Venues" },
  { id: "settings", label: "Settings" },
  { id: "integrations", label: "Integrations" },
  { id: "tablets", label: "Tablets" },
  { id: "activity", label: "Activity" },
  { id: "data", label: "Data" },
];

/**
 * Screen 12 — Organisation. Owners and admins only; the registry hides it
 * from everybody else and every route behind it re-checks `org:manage`, so
 * hiding the tab is a courtesy rather than the control.
 *
 * One fetch backs all three tabs, and every mutation refetches it rather than
 * patching local state: these are small documents, changes here move somebody
 * else's access, and a stale members table is exactly the thing you must not
 * make decisions from.
 */
export function OrgScreen({ params }: ScreenProps) {
  const app = useApp();
  // Tabs that belong to a feature the organisation does not have (D14).
  const tabShown = (id: Tab) =>
    id === "tablets"
      ? hasFeature(app.me, "tablets")
      : id === "integrations"
        ? hasFeature(app.me, "square") || hasFeature(app.me, "dext")
        : true;
  const [tab, setTab] = useState<Tab>(
    TABS.some((t) => t.id === params.tab) && tabShown(params.tab as Tab) ? (params.tab as Tab) : "members",
  );
  const [data, setData] = useState<OrgOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  /** Which control is mid-flight: a user id, or "invite" / "venue" / "settings". */
  const [busy, setBusy] = useState<string | null>(null);
  /** Set once a change lands that alters what the signed-in person may do. */
  const [reloadNeeded, setReloadNeeded] = useState(false);

  // The open tab is part of the address, so a reload or a shared link opens
  // on it. Members, the first tab, is the bare /organisation.
  const { go } = app;
  useEffect(() => {
    go({ kind: "org", params: tab === "members" ? {} : { tab } }, { replace: true });
  }, [tab, go]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await orgApi.overview(app.token));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the organisation");
    }
  }, [app.token]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Run a mutation, then refetch.
   *
   * `touchesMe` covers the changes that alter the caller's own access. A venue
   * assignment shows up after `refreshVenue()`; an org role is baked into the
   * `me` payload App.tsx fetched at sign-in, so that one needs a reload and
   * the banner says so rather than leaving somebody wondering why the nav did
   * not change.
   */
  const run = useCallback(
    async (
      key: string,
      action: () => Promise<string | null>,
      touchesMe: "no" | "venues" | "roles" = "no",
    ) => {
      setBusy(key);
      setError(null);
      setMsg(null);
      try {
        const note = await action();
        await load();
        if (touchesMe !== "no") await app.refreshVenue();
        if (touchesMe === "roles") setReloadNeeded(true);
        if (note) setMsg(note);
      } catch (err) {
        setError(
          err instanceof OrgApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "That did not work",
        );
      } finally {
        setBusy(null);
      }
    },
    [app, load],
  );

  if (error && !data) {
    return (
      <div className="pad">
        <div className="msg err">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="pad label">Loading the organisation…</div>;
  }

  const me = data.members.find((m) => m.isYou);
  const owners = data.members.filter((m) => m.orgRole === "owner");
  const unassignedPeople = data.members.filter(
    (m) => m.venues.length === 0 && m.orgRole === "member",
  );

  const isMe = (userId: string) => userId === app.me.userId;

  return (
    <div className="screen">
      <div className="row screen-head" style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div className="label">
            {data.venues.length}{" "}
            {data.venues.length === 1 ? "venue" : "venues"} · {data.members.length} people
          </div>
          <div className="serif" style={{ fontSize: 28, lineHeight: 1.1, marginTop: 4 }}>
            {data.org.name}
          </div>
        </div>
        <span className="spacer" />
        <span className="org-foot-note">{data.org.orgId}</span>
      </div>

      <div className="org-tabs">
        {TABS.filter((t) => tabShown(t.id)).map((t) => (
          <button
            key={t.id}
            aria-current={tab === t.id}
            onClick={() => {
              setTab(t.id);
              setMsg(null);
            }}
          >
            {t.label}
            {t.id === "members" && <span className="count">{data.members.length}</span>}
            {t.id === "venues" && <span className="count">{data.venues.length}</span>}
          </button>
        ))}
      </div>

      <div className="split">
        <section className="pane">
          <div className="scroll pad">
            {error && <div className="msg err" style={{ marginBottom: 16 }}>{error}</div>}
            {msg && <div className="msg ok" style={{ marginBottom: 16 }}>{msg}</div>}
            {reloadNeeded && (
              <div className="msg" style={{ marginBottom: 16 }}>
                An org role changed. Reload the page for it to take effect here — roles are read
                once at sign-in.
              </div>
            )}

            {tab === "members" && (
              <Members
                members={data.members}
                venues={data.venues}
                busy={busy}
                onInvite={(body) =>
                  run(
                    "invite",
                    async () => {
                      const result = await orgApi.invite(app.token, body);
                      return result.invitationSent
                        ? `Invited ${body.email}. Cognito has emailed them a temporary password.`
                        : `${body.email} already had a sign-in, so no email was sent. They are now a member.`;
                    },
                  )
                }
                onOrgRole={(userId, orgRole) =>
                  run(
                    userId,
                    async () => {
                      await orgApi.setOrgRole(app.token, userId, orgRole);
                      return `Org role set to ${orgRole}.`;
                    },
                    isMe(userId) ? "roles" : "no",
                  )
                }
                onAssign={(userId, venueId, venueRole) =>
                  run(
                    userId,
                    async () => {
                      await orgApi.assign(app.token, userId, venueId, venueRole);
                      return `Assigned as ${venueRole}.`;
                    },
                    isMe(userId) ? "venues" : "no",
                  )
                }
                onUnassign={(userId, venueId) =>
                  run(
                    userId,
                    async () => {
                      await orgApi.unassign(app.token, userId, venueId);
                      return "Taken off that venue.";
                    },
                    isMe(userId) ? "venues" : "no",
                  )
                }
                onRemove={(member: OrgMember) =>
                  run(
                    member.userId,
                    async () => {
                      const label = member.displayName || member.email || member.userId;
                      if (!window.confirm(`Remove ${label} from ${data.org.name}?`)) return null;
                      const result = await orgApi.removeMember(app.token, member.userId);
                      return `${label} removed. ${result.note}`;
                    },
                    isMe(member.userId) ? "roles" : "no",
                  )
                }
              />
            )}

            {tab === "venues" && (
              <Venues
                venues={data.venues}
                busy={busy}
                onCreate={(body) =>
                  run("venue", async () => {
                    const { venue } = await orgApi.createVenue(app.token, body);
                    return `Created ${venue.name} (${venue.venueId}). Nobody is assigned to it yet.`;
                  })
                }
                onSave={(venueId, body) =>
                  run(venueId, async () => {
                    const res = await orgApi.saveVenue(app.token, venueId, body);
                    // A changed day cut-off or timezone re-files the till's recent nights.
                    return res.nightsRetotalled
                      ? `Venue saved. ${res.nightsRetotalled} nights of till sales were re-totalled on its new clock.`
                      : "Venue saved.";
                  }, "venues")
                }
              />
            )}

            {tab === "settings" && (
              <Settings
                orgName={data.org.name}
                settings={data.org.settings}
                busy={busy}
                onSave={(body) =>
                  run("settings", async () => {
                    const result = await orgApi.saveSettings(app.token, body);
                    const totalMoved = result.prepTaskMoves.reduce((n, m) => n + m.moved, 0);
                    const movedNote =
                      totalMoved > 0
                        ? ` Moved ${totalMoved} task${totalMoved === 1 ? "" : "s"} to the renamed tab${result.prepTaskMoves.filter((m) => m.moved > 0).length === 1 ? "" : "s"}.`
                        : "";
                    return (
                      (result.clampedToPlan
                        ? `Saved. The AI cap was lowered to your plan ceiling, ${money(result.settings.aiPlanCeilingCents / 100)}.`
                        : "Settings saved.") + movedNote
                    );
                  })
                }
              />
            )}

            {/* Square and Dext keep their own fetches and messages; only the
                venue list comes from the overview. */}
            {tab === "integrations" && <Integrations venues={data.venues} />}
            {tab === "tablets" && <Tablets venues={data.venues} members={data.members} />}
            {/* Its own fetch, paged; the overview only lends it names. */}
            {tab === "activity" && <Activity venues={data.venues} members={data.members} />}
            {tab === "data" && <DataExport />}
          </div>
        </section>

        <aside className="aside">
          <div className="scroll pad" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <div className="label" style={{ marginBottom: 8 }}>You</div>
              <div style={{ fontSize: 14, lineHeight: 1.55 }}>
                {me?.displayName || app.email}
                <span className="muted"> · {me?.orgRole ?? app.me.org.role ?? "no org role"}</span>
              </div>
            </div>
            <div className="rule" />
            <div className="kv"><span className="k">Venues</span><span className="v">{data.venues.length}</span></div>
            <div className="kv"><span className="k">People</span><span className="v">{data.members.length}</span></div>
            <div className="kv"><span className="k">Owners</span><span className="v">{owners.length}</span></div>
            <div className="kv">
              <span className="k">AI cap</span>
              <span className="v">
                {money(data.org.settings.aiMonthlyLimitCents / 100)} of{" "}
                {money(data.org.settings.aiPlanCeilingCents / 100)}
              </span>
            </div>
            <div className="kv">
              <span className="k">Wastage reasons</span>
              <span className="v">{data.org.settings.wastageReasons.length}</span>
            </div>
            <div className="kv">
              <span className="k">Prep tabs</span>
              <span className="v">{data.org.settings.prepTabs.length}</span>
            </div>

            {owners.length === 1 && (
              <div>
                <div className="label" style={{ marginBottom: 8 }}>One owner</div>
                <div className="org-note">
                  {owners[0]!.displayName || owners[0]!.email} is the only owner, so their role
                  cannot be changed and they cannot be removed. Make somebody else an owner to
                  free that up.
                </div>
              </div>
            )}

            {unassignedPeople.length > 0 && (
              <div>
                <div className="label" style={{ marginBottom: 8 }}>
                  {unassignedPeople.length} with no venue
                </div>
                <div className="org-note">
                  An org role of <span className="mono">member</span> grants nothing on its own —
                  these people reach no venue until they are assigned to one.
                </div>
              </div>
            )}
          </div>
          <div className="foot">
            <button className="btn" onClick={() => void load()} disabled={busy !== null}>
              Refresh
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
